import React from "react";
import {
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import { ScheduleWindow } from "@/context/SharedSchedulesContext";

const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

interface Props {
  name: string;
  timezone: string;
  windows: ScheduleWindow[];
  onNameChange: (name: string) => void;
  onTimezoneChange: (timezone: string) => void;
  onWindowsChange: (windows: ScheduleWindow[]) => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
}

export default function ScheduleEditor({
  name,
  timezone,
  windows,
  onNameChange,
  onTimezoneChange,
  onWindowsChange,
  onSave,
  onCancel,
  saving
}: Props) {
  function addWindow() {
    onWindowsChange([
      ...windows,
      { daysOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], fromTime: "09:00", toTime: "17:00" }
    ]);
  }

  function updateWindow(index: number, patch: Partial<ScheduleWindow>) {
    onWindowsChange(windows.map((w, i) => (i === index ? { ...w, ...patch } : w)));
  }

  function removeWindow(index: number) {
    onWindowsChange(windows.filter((_, i) => i !== index));
  }

  function toggleDay(index: number, day: string, checked: boolean) {
    const w = windows[index];
    const days = checked
      ? [...w.daysOfWeek, day]
      : w.daysOfWeek.filter((d) => d !== day);
    updateWindow(index, { daysOfWeek: days });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-slate-700">Schedule Name</label>
        <input
          className="input w-full"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. Business Hours"
          maxLength={200}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-slate-700">
          Timezone{" "}
          <span className="text-slate-400 font-normal text-xs">
            (optional — defaults to site timezone)
          </span>
        </label>
        <input
          className="input w-full"
          value={timezone}
          onChange={(e) => onTimezoneChange(e.target.value)}
          placeholder="e.g. America/New_York"
          maxLength={100}
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-slate-700">
            Time Windows
          </label>
          <SecondaryBlueButton label="+ Add Window" onClick={addWindow} size="sm" />
        </div>

        {windows.length === 0 && (
          <p className="text-sm text-slate-400 italic">
            No windows defined — add at least one time range.
          </p>
        )}

        {windows.map((w, i) => (
          <div key={i} className="border border-slate-200 rounded p-3 flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {DAY_NAMES.map((day) => (
                <label key={day} className="flex items-center gap-1 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={w.daysOfWeek.includes(day)}
                    onChange={(e) => toggleDay(i, day, e.target.checked)}
                    className="cursor-pointer"
                  />
                  {day.slice(0, 3)}
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">From</label>
              <input
                type="time"
                className="input"
                value={w.fromTime}
                onChange={(e) => updateWindow(i, { fromTime: e.target.value })}
              />
              <label className="text-sm text-slate-600">To</label>
              <input
                type="time"
                className="input"
                value={w.toTime}
                onChange={(e) => updateWindow(i, { toTime: e.target.value })}
              />
              <button
                className="ml-auto text-red-400 hover:text-red-600 text-sm"
                onClick={() => removeWindow(i)}
                aria-label="Remove window"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-2">
        <PrimaryBlueButton
          label={saving ? "Saving…" : "Save Schedule"}
          onClick={onSave}
          disabled={saving || !name.trim()}
        />
        <SecondaryBlueButton label="Cancel" onClick={onCancel} />
      </div>
    </div>
  );
}
